/**
 * The rules a Notice, Event or Poll must meet before it can be sent. The Create notice
 * wizard checks its steps with these, and Ask Noticeboard checks what it's told with
 * the same ones, so the two never disagree. Each returns the problem to show, or
 * undefined when the value is fine.
 */
import { POLL_MAX_OPTIONS, POLL_MIN_OPTIONS } from "../polls/types";
import {
  TIME_OF_DAY,
  getEventEndBeforeStartErrors,
  getEventStartBeforeTodayErrors,
  getEventStartDateTimeMs,
  type EventDateFields,
} from "../../utils/eventDateValidation";
import { earliestSeriesEndDate, formatLocalDate, toLocalDay } from "../events/eventRecurrence";
import { hasPlaceholders, placeholdersErrorMessage } from "../../utils/noticePlaceholders";
import {
  NOTICE_BODY_VISIBLE_MAX,
  NOTICE_BODY_VISIBLE_MIN,
  htmlToPlainText,
  noticeBodyVisibleLength,
} from "../../utils/noticeBodyHtml";

export const NOTICE_TITLE_MIN = 3;
export const NOTICE_TITLE_MAX = 100;

/** A Title, 3 to 100 characters, with no Configurable values left to fill in. */
export function titleProblem(title: string): string | undefined {
  const trimmed = title.trim();
  if (!trimmed) return "Title is required";
  if (trimmed.length < NOTICE_TITLE_MIN) return `Title must be at least ${NOTICE_TITLE_MIN} characters`;
  if (trimmed.length > NOTICE_TITLE_MAX) return `Title must be at most ${NOTICE_TITLE_MAX} characters`;
  if (hasPlaceholders(title)) return placeholdersErrorMessage(title);
  return undefined;
}

/** A message, 10 to 1000 characters as shown (formatting doesn't count), with no Configurable values left to fill in. */
export function messageProblem(body: string): string | undefined {
  const visible = noticeBodyVisibleLength(body);
  if (!visible) return "Content is required";
  if (visible < NOTICE_BODY_VISIBLE_MIN) return `Content must be at least ${NOTICE_BODY_VISIBLE_MIN} characters`;
  if (visible > NOTICE_BODY_VISIBLE_MAX) return `Content must be at most ${NOTICE_BODY_VISIBLE_MAX} characters`;
  const plain = htmlToPlainText(body);
  if (hasPlaceholders(plain)) return placeholdersErrorMessage(plain);
  return undefined;
}

/** An Event's message: it can be left empty, but if written it follows the message rules. */
export function eventMessageProblem(body: string): string | undefined {
  return noticeBodyVisibleLength(body) ? messageProblem(body) : undefined;
}

/** An Event's day: today or later. */
export function eventDayProblem(day: Date | null | undefined, now: Date = new Date()): string | undefined {
  if (!day || Number.isNaN(new Date(day).getTime())) return "Event start date is required";
  return getEventStartBeforeTodayErrors({ eventStartDate: day }, now).eventStartDate;
}

/** An Event's start time, which it needs, in a form it can read. */
export function eventTimeProblem(time: string | undefined): string | undefined {
  if (!time?.trim()) return "Event start time is required";
  if (!TIME_OF_DAY.test(time)) return "Please enter a valid time (HH:MM)";
  return undefined;
}

/** An Event's place, which it needs. */
export function eventPlaceProblem(place: string | undefined): string | undefined {
  return place?.trim() ? undefined : "Event location is required";
}

/** An Event's end day, if it has one, not before its start day. */
export function eventEndProblem(event: EventDateFields): string | undefined {
  return getEventEndBeforeStartErrors(event).eventEndDate;
}

/** An Event's end time not before its start time on the same day. No end date means the same day. */
export function eventEndTimeProblem(event: EventDateFields): string | undefined {
  return getEventEndBeforeStartErrors(event).eventEndTime;
}

/**
 * An RSVP deadline, if RSVP is on and it has one, no later than the Event. With
 * an end date, that's the end (an end with no time is read as that day, like
 * the backend). With an end time only, it's that time on the start day.
 * Otherwise it's the start.
 */
export function rsvpDeadlineProblem(event: {
  rsvpEnabled?: boolean;
  rsvpClosesAt?: string;
  eventStartDate?: Date | string | null;
  eventStartTime?: string;
  eventEndDate?: Date | string | null;
  eventEndTime?: string;
}): string | undefined {
  if (!event.rsvpEnabled || !event.rsvpClosesAt?.trim()) return undefined;
  const deadline = new Date(event.rsvpClosesAt).getTime();
  if (!Number.isFinite(deadline)) return "RSVP deadline is invalid";
  const endTime = event.eventEndTime?.trim();
  if (event.eventEndDate) {
    const endDate = event.eventEndDate instanceof Date ? event.eventEndDate : new Date(event.eventEndDate);
    if (Number.isNaN(endDate.getTime())) return undefined;
    const day = formatLocalDate(endDate);
    const endMs = new Date(endTime ? `${day}T${endTime.padStart(5, "0")}` : day).getTime();
    return Number.isFinite(endMs) && deadline > endMs
      ? "RSVP deadline cannot be after the event end"
      : undefined;
  }
  if (endTime) {
    const endMs = getEventStartDateTimeMs(event.eventStartDate, endTime.padStart(5, "0"));
    return endMs !== null && deadline > endMs
      ? "RSVP deadline cannot be after the event end"
      : undefined;
  }
  const startMs = getEventStartDateTimeMs(event.eventStartDate, event.eventStartTime?.trim().padStart(5, "0"));
  return startMs !== null && deadline > startMs
    ? "RSVP deadline cannot be after the event starts"
    : undefined;
}

/** A repeating Event needs an end date that includes at least one more occurrence. */
export function repeatProblem(
  frequency: string | undefined,
  until: Date | string | null | undefined,
  start?: Date | string | null,
): string | undefined {
  if (!frequency || frequency === "none") return undefined;
  if (!until) return "Choose an end date for the series";
  const earliest = earliestSeriesEndDate(start, frequency);
  if (!earliest) return undefined;
  const untilDay = toLocalDay(until);
  if (!untilDay) return "Choose an end date for the series";
  if (untilDay.getTime() >= earliest.getTime()) return undefined;
  const pad = (n: number) => String(n).padStart(2, "0");
  const formatted = `${pad(earliest.getDate())}/${pad(earliest.getMonth() + 1)}/${earliest.getFullYear()}`;
  return `Pick an end date on or after ${formatted} so this series includes at least two events`;
}

/** A Poll's question, which it needs. */
export function pollQuestionProblem(question: string | undefined): string | undefined {
  return question?.trim() ? undefined : "Question is required";
}

/** A Poll's choices: 2 to 50, each different (blank ones are left out). */
export function pollChoicesProblem(labels: string[]): string | undefined {
  const choices = labels.map((l) => l.trim()).filter(Boolean);
  if (choices.length < POLL_MIN_OPTIONS) return `Add at least ${POLL_MIN_OPTIONS} options`;
  if (choices.length > POLL_MAX_OPTIONS) return `At most ${POLL_MAX_OPTIONS} options`;
  if (new Set(choices.map((c) => c.toLowerCase())).size !== choices.length) return "Option labels must be unique";
  return undefined;
}
